import { useEffect, useId, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { apiErrorText } from '../../../../utils';
import { carrierIssuesPolicyNumbers } from './carrierPolicyNumber';

type PolicyNumberQuote = {
  status?: string;
  policyNumber?: string | null;
  product?: { id?: string } | null;
};

/**
 * The carrier's policy number, for a quote of a product whose carrier issues them. It is editable
 * while the quote is pending and the user may adjust it, read-only otherwise, and saved on its own.
 * A refused save shows the server's message. Other products show nothing.
 */
export function QuotePolicyNumberField({
  quote,
  canEdit,
  onSave,
  onSaved,
}: {
  quote: PolicyNumberQuote;
  canEdit: boolean;
  onSave: (policyNumber: string) => Promise<unknown>;
  onSaved: () => unknown;
}) {
  const held = quote.policyNumber ?? '';
  const [value, setValue] = useState(held);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const id = useId();

  useEffect(() => {
    setValue(held);
    setError(null);
  }, [held]);

  if (!carrierIssuesPolicyNumbers(quote.product)) {
    return null;
  }

  const editable = canEdit && quote.status === 'pending';
  if (!editable) {
    return (
      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-medium">Policy number</span>
        <span className="text-sm text-muted-foreground">{held || 'Not set'}</span>
      </div>
    );
  }

  const trimmed = value.trim();
  const canSave = !saving && trimmed.length > 0 && trimmed !== held;

  const save = async () => {
    if (!canSave) return;
    setError(null);
    setSaving(true);
    try {
      await onSave(trimmed);
    } catch (err) {
      setError(apiErrorText(err, 'Saving the policy number failed'));
      setSaving(false);
      return;
    }
    setSaving(false);
    onSaved();
  };

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>Policy number</Label>
      <div className="flex items-center gap-2">
        <Input id={id} className="max-w-60" value={value} onChange={(e) => setValue(e.target.value)} disabled={saving} />
        <Button onClick={save} disabled={!canSave}>
          {saving ? <Spinner /> : null}
          Save
        </Button>
      </div>
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
    </div>
  );
}
